'use client';

import React, { FC, useMemo } from 'react';

import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import { desktop, hugeDesktop, largeDesktop, minitablet, mobile, tablet } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { useParams, usePathname } from 'next/navigation';
import { productsInSkuOrder } from 'helpers/algolia/product-ids';
import ProductTile from '../product-tile';
import { Tiempos } from 'fonts';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export interface ProductTileCMSProps {
  title?: string;
  /** Server-fetched Algolia hits (backend action → Redis). See the slider-cms tastic. */
  hits: any[];
  /** The item-codes the CMS entry listed, in the author's order. */
  skuCodes: string[];
}

/**
 * The V1 "Featured Products From CMS" carousel. A pure view: its products are fetched on the SERVER
 * (see frontastic/tastics/products/product-tile-cms), so nothing here queries Algolia and the tiles
 * are in the SSR HTML.
 *
 * Ordering and variant selection are the shared `productsInSkuOrder` helper, the same one the V2
 * `section_with_product_slider` 'Product IDs' block uses — this used to carry its own copy, which
 * differed in how it matched a SKU.
 */
const ProductTilesCMS: FC<ProductTileCMSProps> = ({ title, hits, skuCodes }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isHugeDesktop] = useMediaQuery(hugeDesktop);

  const currentURL = usePathname();
  const { locale } = useParams();
  const isProductDetailPage = isPdpUrl(currentURL);

  const products = useMemo(() => productsInSkuOrder(hits, skuCodes, locale as string), [hits, skuCodes, locale]);

  return (
    <div
      id="related-products"
      className={`py-48 pl-24 xl:pb-80 ${products.length > 1 ? '2xl:pb-178' : '2xl:pb-120'} xl:pl-60 2xl:pl-80 md:pl-28 lg:pl-48 md:py-48 xl:pt-60 2xl:pt-120 ${
        isProductDetailPage
          ? 'relative left-full w-screen -translate-x-full bg-[#EDEDED] px-48 md:pb-1 md:pt-100 lg:grid lg:grid-cols-12 lg:pl-0 xl:pb-153 xl:pt-153'
          : 'md:pl-40'
      }`}
    >
      <div className={isProductDetailPage ? 'lg:col-span-9 lg:col-start-4' : ''}>
        <Typography
          id="related-products-title"
          className={`pb-52 lg:pb-36 text-32 font-medium text-primary-black xl:text-36 2xl:text-48 ${Tiempos.className}`}
          as="h3"
        >
          {title}
        </Typography>
        <div className="product-slider-container relative w-full">
          <Slider
            className="mb-48"
            slidesPerView={1.2}
            slidesPerGroup={1}
            dots={false}
            arrows={
              products.length > 1 &&
              (isTabletSize || isDesktopSize || isMiniTabletSize) &&
              !(isDesktopSize && products.length <= 3) &&
              !(isHugeDesktop && products.length <= 4)
            }
            innerArrows
            autoplay={false}
            solidArrows
            allowArrowsOnTouchDevice
            nextButtonStyles={{ top: '110%', left: 50, padding: '10px' }}
            prevButtonStyles={{ top: '110%', left: 0, padding: '10px' }}
            allowTouchMove
            spaceBetween={8}
            breakpoints={{
              [mobile]: { slidesPerView: 2.1, spaceBetween: 16 },
              [tablet]: { slidesPerView: 2.1, spaceBetween: 16 },
              [desktop]: { slidesPerView: 3.5, spaceBetween: 16 },
              [hugeDesktop]: { slidesPerView: 4.3, spaceBetween: 16 },
              [largeDesktop]: { slidesPerView: 3.5, spaceBetween: 16 },
            }}
          >
            {products.map((product, index) => (
              <ProductTile key={product.productId ?? product.slug ?? index} product={product} layout="compact" />
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default ProductTilesCMS;
